import { ArrowClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Stack } from "@volca/ui/components/ui/stack";
import { useCanManageProfitability } from "./access";
import type { ProfitabilityRun } from "./types";
import { useReportDownload } from "./use-report-download";
import { useRerunReport } from "./use-rerun-report";
import { runDates } from "./utils";

/**
 * The report header's right side. Ask Volca is the thing an owner came to do with
 * a finished report, so it keeps a button; downloading and rerunning are
 * occasional, so they share the menu, the same shape the payroll header uses.
 */
export const ReportActions = ({
  businessId,
  run,
  onOpenChat,
  chatLabel = "Ask AI",
}: {
  businessId: string;
  run: ProfitabilityRun;
  onOpenChat: () => void;
  chatLabel?: "Ask AI" | "Ask Volca";
}) => {
  const { download, downloading } = useReportDownload(businessId, run);
  const { rerun, rerunning } = useRerunReport(businessId, run);
  const busy = downloading || rerunning;
  // Running a period again is finance's; a manager downloads and asks.
  const canRerun = useCanManageProfitability() && !run.draft_sample;

  return (
    <Stack direction="row" align="center" gap={2}>
      <Button variant="secondary" onClick={onOpenChat}>
        <ChatCircleIcon data-icon="inline-start" />
        {chatLabel}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="secondary"
              size="icon"
              aria-label="More actions"
              loading={busy}
              loadingLabel="More actions, in progress"
            />
          }
        >
          <DotsThreeIcon className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuItem disabled={busy} onClick={download}>
            {!downloading && <DownloadSimpleIcon className="h-4 w-4" />}
            Download XLSX
          </DropdownMenuItem>
          {canRerun && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={busy} onClick={() => rerun()}>
                {!rerunning && <ArrowClockwiseIcon className="h-4 w-4" />}
                {runDates(run)
                  ? "Run these dates again"
                  : "Run this report again"}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <span role="status" className="sr-only">
        {downloading
          ? "Download XLSX in progress"
          : rerunning
            ? `${runDates(run) ? "Run these dates again" : "Run this report again"} in progress`
            : ""}
      </span>
    </Stack>
  );
};
